import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { SignalMetadataEntry, InteractionNode } from '@/types';
import { InfoTooltip } from '@/components/shared';
import { celsiusToFahrenheit } from '@/utils/temperature';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SignalContribution {
  signal: string;
  raw_value: number;
  risk_score: number;
  weighted_contribution: number;
  direction: string;
  explanation: string;
}

interface SignalFlowNetworkProps {
  riskScore: number;
  tier: string;
  previousRiskScore?: number;
  contributions: SignalContribution[];
  interactionPenalties: string[];
  signalMetadata: Record<string, SignalMetadataEntry>;
  interactionNodes: InteractionNode[];
  isDark: boolean;
  connectedSources?: string[];
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const VIEW_W = 860;
const MAX_VISIBLE_SIGNALS = 7;

// Column x positions
const SRC_X = 10;
const SRC_W = 85;
const SRC_H = 32;

const SIG_X = 170;
const SIG_W = 120;
const SIG_H = 28;

const INT_X = 420;
const INT_W = 160;
const INT_H = 44;

const INS_X = 700;
const INS_W = 140;
const INS_H = 80;

// Dividers
const DIV1_X = 140;
const DIV2_X = 370;
const DIV3_X = 660;

// SIG_SPACING computed dynamically in layout

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function edgeColor(riskScore: number, dark: boolean): string {
  if (riskScore < 0.15) return dark ? '#4ade80' : '#16a34a';
  if (riskScore < 0.3) return dark ? '#ffffff' : '#1a1a1a';
  return '#ef4444';
}

function edgeThickness(weight: number): number {
  return Math.max(0.5, Math.min(3, weight * 15));
}

function edgeOpacity(riskScore: number, weight: number): number {
  if (riskScore < 0.15) return 0.15 + weight * 0.3;
  if (riskScore < 0.3) return 0.08 + weight * 0.15;
  return 0.2 + Math.min(weight * 2, 0.5);
}

function hasGlow(weight: number): boolean {
  return weight > 0.03;
}

function formatWithUnit(signal: string, value: number, meta: SignalMetadataEntry): string {
  const unit = meta.unit || '';
  if (unit === 'ms') return `${value.toFixed(0)}ms`;
  if (unit === 'hrs' || unit === 'h') return `${value.toFixed(1)}h`;
  if (unit === 'bpm') return `${value.toFixed(0)}bpm`;
  if (unit === '/10') return `${value.toFixed(0)}/10`;
  if (unit === '%') return `${value.toFixed(0)}%`;
  if (unit === 'br/m' || unit === 'brpm') return `${value.toFixed(1)}brpm`;
  if (unit === '°C' || unit === 'C' || unit === 'degC') return `${celsiusToFahrenheit(value).toFixed(1)}°F`;
  if (unit === 'ratio') return value.toFixed(2);
  if (unit) return `${value.toFixed(1)} ${unit}`;
  return value.toFixed(1);
}

function extractMultiplier(penalty: string): number | null {
  const m = penalty.match(/\(x([\d.]+)\)/);
  return m ? parseFloat(m[1]) : null;
}

const SOURCE_LABELS: Record<string, string> = {
  wearable: 'Wearable',
  checkin: 'Check-in',
  training: 'Training',
  catapult: 'Catapult GPS',
};

const DEVICE_LABELS: Record<string, string> = {
  whoop: 'WHOOP',
  garmin: 'Garmin',
  oura: 'Oura',
  coros: 'COROS',
  polar: 'Polar',
  suunto: 'Suunto',
};

const SOURCE_ORDER = ['wearable', 'catapult', 'checkin', 'training'];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SignalFlowNetwork: React.FC<SignalFlowNetworkProps> = ({
  riskScore,
  tier,
  previousRiskScore,
  contributions,
  interactionPenalties,
  signalMetadata,
  interactionNodes,
  isDark,
  connectedSources,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Mobile responsiveness: collapse diagram behind a toggle on small screens
  const [isMobile, setIsMobile] = useState(false);
  const [mobileCollapsed, setMobileCollapsed] = useState(true);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Hover-highlight state: tracks which element the user is hovering
  const [hoveredElement, setHoveredElement] = useState<{
    type: 'source' | 'signal' | 'interaction' | 'insight';
    id: string;
  } | null>(null);

  const layout = useMemo(() => {
    // Determine visible signals — filter, sort by contribution, cap at MAX_VISIBLE_SIGNALS
    // Exclude data-quality signals that aren't physiological (they add noise to the flow diagram)
    const DATA_QUALITY_SIGNALS = new Set(['wearable_missing_rate_7d']);
    const allVisible = contributions
      .filter(c => {
        if (DATA_QUALITY_SIGNALS.has(c.signal)) return false;
        const meta = signalMetadata[c.signal];
        if (!meta) return false;
        return c.risk_score > 0.1 || c.weighted_contribution > 0.005;
      })
      .sort((a, b) => b.weighted_contribution - a.weighted_contribution);

    const truncatedCount = expanded ? 0 : Math.max(0, allVisible.length - MAX_VISIBLE_SIGNALS);
    const visibleContribs = expanded ? allVisible : allVisible.slice(0, MAX_VISIBLE_SIGNALS);
    const canCollapse = expanded && allVisible.length > MAX_VISIBLE_SIGNALS;

    // Dynamic spacing based on signal count
    const VIEW_H_BASE = 420;
    const sigSpacing = Math.max(32, Math.min(42, (VIEW_H_BASE - 60) / Math.max(visibleContribs.length, 1)));

    // Assign y positions to signals, centered vertically
    const totalSigHeight = visibleContribs.length * sigSpacing;
    const sigStartY = Math.max(20, (VIEW_H_BASE - totalSigHeight) / 2 + 10);

    const signalPositions: Record<string, number> = {};
    visibleContribs.forEach((c, i) => {
      signalPositions[c.signal] = sigStartY + i * sigSpacing + SIG_H / 2;
    });

    // Determine visible interactions — use ALL contributions (not just visible/truncated)
    // so interaction nodes activate even when their signals are behind "+N more"
    const allSignalKeys = new Set(allVisible.map(c => c.signal));
    const visibleInteractions = interactionNodes.filter(node => {
      const presentSignals = node.signals.filter(s => allSignalKeys.has(s));
      return presentSignals.length >= 1;
    });

    // Assign y positions to interactions — distributed across the signal column height
    const sigColumnTop = sigStartY;
    const sigColumnBottom = sigStartY + (visibleContribs.length - 1) * sigSpacing + SIG_H;
    const intAvailableHeight = sigColumnBottom - sigColumnTop;
    const intSpacing = intAvailableHeight / Math.max(visibleInteractions.length + 1, 2);

    const interactionPositions: Record<string, number> = {};
    visibleInteractions.forEach((node, i) => {
      interactionPositions[node.id] = sigColumnTop + intSpacing * (i + 1) - INT_H / 2;
    });

    // Group signals by source
    const sourceGroups: Record<string, string[]> = {};
    visibleContribs.forEach(c => {
      const meta = signalMetadata[c.signal];
      const src = (meta?.sources?.[0]) || 'wearable';
      if (!sourceGroups[src]) sourceGroups[src] = [];
      sourceGroups[src].push(c.signal);
    });

    // Compute source node Y (center of its group)
    const sourcePositions: Record<string, number> = {};
    for (const src of SOURCE_ORDER) {
      const signals = sourceGroups[src] || [];
      if (signals.length === 0) continue;
      const ys = signals.map(s => signalPositions[s]);
      const avgY = ys.reduce((a, b) => a + b, 0) / ys.length;
      sourcePositions[src] = avgY;
    }

    // Enforce minimum vertical gap between sensor nodes so they don't overlap
    const sortedSources = Object.entries(sourcePositions)
      .sort((a, b) => a[1] - b[1]);
    for (let i = 1; i < sortedSources.length; i++) {
      const prevBottom = sortedSources[i - 1][1] + SRC_H / 2;
      const currTop = sortedSources[i][1] - SRC_H / 2;
      if (currTop < prevBottom + 12) {
        sortedSources[i][1] = prevBottom + 12 + SRC_H / 2;
        sourcePositions[sortedSources[i][0]] = sortedSources[i][1];
      }
    }

    // Add extra room for "+N more" indicator when signals are truncated
    const truncatedPadding = truncatedCount > 0 ? 20 : 0;

    // Insight node Y — centered relative to the signal column range
    const insightY = (sigColumnTop + sigColumnBottom) / 2 - INS_H / 2;

    // Compute last interaction bottom for content height
    const intPositionValues = Object.values(interactionPositions);
    const lastInteractionBottom = intPositionValues.length > 0
      ? Math.max(...intPositionValues) + INT_H
      : 0;

    // Content height must accommodate all three columns
    const contentHeight = Math.max(
      sigColumnBottom + 40 + truncatedPadding,
      lastInteractionBottom + 40,
      insightY + INS_H + 40,
      300,
    );

    return {
      visibleContribs,
      signalPositions,
      visibleInteractions,
      interactionPositions,
      sourceGroups,
      sourcePositions,
      insightY,
      contentHeight,
      truncatedCount,
      canCollapse,
    };
  }, [contributions, signalMetadata, interactionNodes, expanded]);

  const {
    visibleContribs,
    signalPositions,
    visibleInteractions,
    interactionPositions,
    sourceGroups,
    sourcePositions,
    insightY,
    contentHeight,
    truncatedCount,
    canCollapse,
  } = layout;

  // Compute highlighted set based on hovered element
  const highlightedSet = useMemo(() => {
    if (!hoveredElement) return null;
    const set = {
      sources: new Set<string>(),
      signals: new Set<string>(),
      interactions: new Set<string>(),
      insight: false,
    };

    switch (hoveredElement.type) {
      case 'source': {
        set.sources.add(hoveredElement.id);
        // Find all signals from this source
        visibleContribs.forEach(c => {
          const meta = signalMetadata[c.signal];
          if (meta?.sources?.includes(hoveredElement.id)) {
            set.signals.add(c.signal);
          }
        });
        break;
      }
      case 'signal': {
        set.signals.add(hoveredElement.id);
        // Find source(s) for this signal
        const meta = signalMetadata[hoveredElement.id];
        meta?.sources?.forEach(s => set.sources.add(s));
        // Find interaction(s) this signal feeds into
        visibleInteractions.forEach(node => {
          if (node.signals.includes(hoveredElement.id)) {
            set.interactions.add(node.id);
          }
        });
        // If no interactions matched, it connects directly to insight
        if (set.interactions.size === 0) set.insight = true;
        break;
      }
      case 'interaction': {
        set.interactions.add(hoveredElement.id);
        // Find signals feeding this interaction
        const node = visibleInteractions.find(n => n.id === hoveredElement.id);
        node?.signals.forEach(s => {
          if (signalPositions[s] !== undefined) set.signals.add(s);
        });
        // Interaction always connects to insight
        set.insight = true;
        break;
      }
      case 'insight': {
        set.insight = true;
        // All interactions connect to insight
        visibleInteractions.forEach(n => set.interactions.add(n.id));
        // If no interactions, top signals connect directly
        if (visibleInteractions.length === 0) {
          visibleContribs.slice(0, 3).forEach(c => set.signals.add(c.signal));
        }
        break;
      }
    }
    return set;
  }, [hoveredElement, visibleContribs, signalMetadata, visibleInteractions, signalPositions]);

  // Check if an element is highlighted (no hover = everything visible)
  const isHighlighted = useCallback((type: 'source' | 'signal' | 'interaction' | 'insight', id: string): boolean => {
    if (!highlightedSet) return true;
    switch (type) {
      case 'source': return highlightedSet.sources.has(id);
      case 'signal': return highlightedSet.signals.has(id);
      case 'interaction': return highlightedSet.interactions.has(id);
      case 'insight': return highlightedSet.insight;
    }
  }, [highlightedSet]);

  const dimOpacity = useCallback((type: 'source' | 'signal' | 'interaction' | 'insight', id: string): number => {
    return isHighlighted(type, id) ? 1 : 0.15;
  }, [isHighlighted]);

  const riskPct = Math.round(riskScore * 100);
  const deltaPct = previousRiskScore !== undefined
    ? Math.round((riskScore - previousRiskScore) * 100)
    : null;

  const green = isDark ? '#4ade80' : '#16a34a';
  const tierColors: Record<string, string> = {
    LOW: green,
    MODERATE: '#eab308',
    ELEVATED: '#f97316',
    HIGH: '#ef4444',
    CRITICAL: '#991b1b',
  };
  const tierColor = tierColors[tier] || '#ef4444';

  // Build signal→contribution lookup
  const contribMap: Record<string, SignalContribution> = {};
  visibleContribs.forEach(c => { contribMap[c.signal] = c; });

  // Tooltip state
  const [tooltip, setTooltip] = useState<{ lines: string[]; x: number; y: number } | null>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  const showTooltip = useCallback((e: React.MouseEvent, lines: string[]) => {
    const container = svgContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltip({
      lines,
      x: e.clientX - rect.left + container.scrollLeft,
      y: e.clientY - rect.top + container.scrollTop,
    });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Section label */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px',
      }}>
        <span style={{
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}>
          Signal Flow
        </span>
        <InfoTooltip
          title="How to read this diagram"
          body="Sensors (left) feed signals, which flow into interaction patterns, converging into the final risk score (right). Each signal shows its current value and direction. The badge (e.g. +4) is the SHAP contribution — how many points that signal adds to or subtracts from the composite risk score. Red borders = risk-increasing, green = protective, gray = neutral."
          isDark={isDark}
          iconSize={3}
          ariaLabel="How to read the signal flow diagram"
        />
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '10px',
        flexWrap: 'wrap',
      }}>
        {[
          { color: isDark ? '#4ade80' : '#16a34a', label: 'Protective', tip: 'Risk < 15%. Signal is reducing overall risk. Badge shows negative points (\u2212X).' },
          { color: isDark ? '#555' : '#999', label: 'Neutral', tip: 'Risk 15\u201330%. Mild contribution to risk score. Badge shows positive points (+X).' },
          { color: '#ef4444', label: 'Risk-increasing', tip: 'Risk > 30%. Signal is meaningfully elevating injury risk. Badge shows positive points (+X).' },
        ].map(({ color, label, tip }) => (
          <div key={label} className="group/legend" style={{ display: 'flex', alignItems: 'center', gap: '5px', position: 'relative', cursor: 'help' }}>
            <div style={{ width: '14px', height: '10px', backgroundColor: color, opacity: 0.8 }} />
            <span className={`text-[11px] transition-colors duration-150 ${isDark ? 'text-neutral-500 group-hover/legend:text-neutral-200' : 'text-neutral-400 group-hover/legend:text-neutral-700'}`}>
              {label}
            </span>
            <div className={`absolute left-0 top-full mt-1.5 z-[9999] w-56 p-3 border shadow-lg opacity-0 invisible pointer-events-none group-hover/legend:opacity-100 group-hover/legend:visible transition-all duration-150 ${isDark ? 'bg-neutral-900 border-neutral-600 text-neutral-200' : 'bg-white border-gray-200 text-gray-900'}`}
              style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 'normal' }}
            >
              <div className="text-xs font-semibold mb-1" style={{ color: isDark ? '#F9FAFB' : '#111827' }}>{label}</div>
              <p className="text-xs leading-relaxed" style={{ color: isDark ? '#9CA3AF' : '#6B7280', margin: 0 }}>{tip}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile collapsed summary */}
      {isMobile && mobileCollapsed ? (
        <div
          onClick={() => setMobileCollapsed(false)}
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--card-border)',
            padding: '16px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
              Signal Flow Network
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {visibleContribs.length} signals → {visibleInteractions.length} interactions → Risk Score
            </div>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0, marginLeft: '12px' }}>Tap to expand →</span>
        </div>
      ) : (
      <>
      {isMobile && (
        <div
          onClick={() => setMobileCollapsed(true)}
          style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'right', marginBottom: '8px', cursor: 'pointer' }}
        >
          ← Collapse diagram
        </div>
      )}
      {/* SVG container */}
      <div ref={svgContainerRef} style={{
        overflowX: 'auto',
        background: 'var(--bg-card)',
        border: '1px solid var(--card-border)',
        padding: '24px 16px',
        position: 'relative',
      }}>
        {/* Tooltip overlay */}
        {tooltip && (
          <div style={{
            position: 'absolute',
            left: Math.min(tooltip.x, (svgContainerRef.current?.clientWidth || 800) - 200),
            top: tooltip.y - 8,
            transform: 'translateY(-100%)',
            background: 'var(--bg-card)',
            border: '1px solid var(--card-border)',
            padding: '8px 12px',
            fontSize: '11px',
            whiteSpace: 'pre-line',
            maxWidth: '320px',
            zIndex: 20,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            {tooltip.lines.map((line, i) => (
              <div key={i} style={{
                color: i === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: i === 0 ? 600 : 400,
                marginTop: i > 0 ? 2 : 0,
              }}>
                {line}
              </div>
            ))}
          </div>
        )}
        <svg
          viewBox={`0 0 ${VIEW_W} ${contentHeight}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          <defs>
            {/* Glow filters */}
            <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Divider lines */}
          {[DIV1_X, DIV2_X, DIV3_X].map(x => (
            <line key={x} x1={x} y1={8} x2={x} y2={contentHeight - 8}
              stroke={isDark ? '#1f1f1f' : '#e5e7eb'} strokeWidth={1} />
          ))}

          {/* Column labels */}
          {[
            { x: SRC_X + SRC_W / 2, label: 'SENSORS' },
            { x: SIG_X + SIG_W / 2, label: 'SIGNALS' },
            { x: INT_X + INT_W / 2, label: 'INTERACTIONS' },
            { x: INS_X + INS_W / 2, label: 'INSIGHT' },
          ].map(col => (
            <text
              key={col.label}
              x={col.x}
              y={12}
              textAnchor="middle"
              style={{ fill: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}
              fontSize={9}
            >
              {col.label}
            </text>
          ))}

          {/* ----------------------------------------------------------------
              LAYER 1: Sensor → Signal edges + sensor nodes
          ----------------------------------------------------------------- */}
          {SOURCE_ORDER.filter(src => sourcePositions[src] !== undefined).map(src => {
            const srcCY = sourcePositions[src];
            const signals = sourceGroups[src] || [];
            const srcLabel = SOURCE_LABELS[src] || src;

            // For the wearable sensor, resolve connected device names for tooltip
            const wearableDevices = src === 'wearable'
              ? (connectedSources || [])
                  .filter(s => s in DEVICE_LABELS)
                  .map(s => DEVICE_LABELS[s] || s)
              : [];

            return (
              <g key={src}>
                {/* Source → Signal edges */}
                {signals.map(signal => {
                  const c = contribMap[signal];
                  if (!c) return null;
                  const meta = signalMetadata[c.signal];
                  const sigY = signalPositions[signal];
                  const color = edgeColor(c.risk_score, isDark);
                  const thickness = edgeThickness(c.weighted_contribution);
                  const opacity = edgeOpacity(c.risk_score, c.weighted_contribution);
                  const glow = hasGlow(c.weighted_contribution);
                  const edgeVisible = isHighlighted('source', src) && isHighlighted('signal', signal);

                  return (
                    <g
                      key={signal}
                      opacity={edgeVisible ? 1 : 0.15}
                      style={{ transition: 'opacity 0.15s ease' }}
                    >
                      <g
                        onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                          `${meta?.label || signal} from ${srcLabel}`,
                          `Contribution: ${(c.weighted_contribution * 100).toFixed(0)}% of score`,
                        ])}
                        onMouseLeave={hideTooltip}
                        cursor="pointer"
                      >
                        <path
                          d={`M${SRC_X + SRC_W},${srcCY} C${SRC_X + SRC_W + 40},${srcCY} ${SIG_X - 20},${sigY} ${SIG_X},${sigY}`}
                          stroke={color}
                          strokeWidth={thickness}
                          strokeOpacity={opacity}
                          fill="none"
                          filter={glow ? (c.risk_score > 0.3 ? 'url(#glow-red)' : c.risk_score < 0.15 ? 'url(#glow-green)' : undefined) : undefined}
                        />
                      </g>
                    </g>
                  );
                })}

                {/* Source node */}
                <g
                  opacity={dimOpacity('source', src)}
                  style={{ transition: 'opacity 0.15s ease' }}
                >
                  <g
                    onMouseEnter={(e: React.MouseEvent) => {
                      showTooltip(e, src === 'wearable'
                        ? [
                            'Wearable',
                            `Provides: ${signals.length} signals`,
                            wearableDevices.length > 0 ? `Connected: ${wearableDevices.join(', ')}` : 'No device connected',
                          ]
                        : [
                            `${srcLabel}`,
                            `Provides: ${signals.length} signals to this assessment`,
                          ]
                      );
                      setHoveredElement({ type: 'source', id: src });
                    }}
                    onMouseLeave={() => {
                      hideTooltip();
                      setHoveredElement(null);
                    }}
                    cursor="pointer"
                  >
                    <rect
                      x={SRC_X}
                      y={srcCY - SRC_H / 2}
                      width={SRC_W}
                      height={SRC_H}
                      style={{ fill: 'var(--accent-primary)' }}
                      stroke={isDark ? '#262626' : '#d4d4d4'}
                      strokeWidth={1}
                      rx={0}
                    />
                    <text
                      x={SRC_X + SRC_W / 2}
                      y={srcCY + 4}
                      textAnchor="middle"
                      style={{ fill: 'var(--text-secondary)' }}
                      fontSize={9}
                    >
                      {srcLabel}
                    </text>
                  </g>
                </g>
              </g>
            );
          })}

          {/* Secondary source edges for multi-source signals */}
          {visibleContribs.map(c => {
            const meta = signalMetadata[c.signal];
            if (!meta?.sources || meta.sources.length <= 1) return null;
            const sigY = signalPositions[c.signal];
            if (sigY === undefined) return null;
            const color = edgeColor(c.risk_score, isDark);
            const thickness = Math.max(edgeThickness(c.weighted_contribution) * 0.6, 0.5);
            const opacity = edgeOpacity(c.risk_score, c.weighted_contribution) * 0.6;

            return meta.sources.slice(1).map(src => {
              const srcCY = sourcePositions[src];
              if (srcCY === undefined) return null;
              const edgeVisible = isHighlighted('source', src) && isHighlighted('signal', c.signal);
              return (
                <g
                  key={`secondary-${src}-${c.signal}`}
                  opacity={edgeVisible ? 1 : 0.15}
                  style={{ transition: 'opacity 0.15s ease' }}
                >
                  <path
                    d={`M${SRC_X + SRC_W},${srcCY} C${SRC_X + SRC_W + 40},${srcCY} ${SIG_X - 20},${sigY} ${SIG_X},${sigY}`}
                    stroke={color}
                    strokeWidth={thickness}
                    strokeOpacity={opacity}
                    fill="none"
                    strokeDasharray="4 3"
                  />
                </g>
              );
            });
          })}

          {/* ----------------------------------------------------------------
              LAYER 2: Signal nodes
          ----------------------------------------------------------------- */}
          {visibleContribs.map(c => {
            const meta = signalMetadata[c.signal];
            const sigY = signalPositions[c.signal];
            const nodeCY = sigY;

            let borderColor = isDark ? '#262626' : '#d4d4d4';
            let valueColor = isDark ? '#555' : '#888';
            if (c.risk_score > 0.3) {
              borderColor = 'rgba(239,68,68,0.4)';
              valueColor = '#ef4444';
            } else if (c.risk_score < 0.15) {
              borderColor = isDark ? 'rgba(74,222,128,0.4)' : 'rgba(22,163,74,0.4)';
              valueColor = green;
            }

            const label = meta?.label || c.signal.replace(/_/g, ' ');
            const rawFormatted = meta ? formatWithUnit(c.signal, c.raw_value, meta) : c.raw_value.toFixed(1);

            return (
              <g
                key={c.signal}
                opacity={dimOpacity('signal', c.signal)}
                style={{ transition: 'opacity 0.15s ease' }}
              >
                <g
                  onMouseEnter={(e: React.MouseEvent) => {
                    const pts = Math.round(c.weighted_contribution * 100);
                    const shapSign = c.risk_score < 0.15 ? `-${pts}` : `+${pts}`;
                    showTooltip(e, [
                      `${label}: ${rawFormatted}`,
                      `Sources: ${(meta?.sources || []).map(s => SOURCE_LABELS[s] || s).join(', ') || 'unknown'}`,
                      `Risk: ${(c.risk_score * 100).toFixed(0)}%`,
                      `SHAP contribution: ${shapSign} points`,
                    ]);
                    setHoveredElement({ type: 'signal', id: c.signal });
                  }}
                  onMouseLeave={() => {
                    hideTooltip();
                    setHoveredElement(null);
                  }}
                  cursor="pointer"
                >
                  <rect
                    x={SIG_X}
                    y={nodeCY - SIG_H / 2}
                    width={SIG_W}
                    height={SIG_H}
                    style={{ fill: 'var(--accent-primary)' }}
                    stroke={borderColor}
                    strokeWidth={1}
                    rx={0}
                  />
                  {/* Signal label */}
                  <text
                    x={SIG_X + 6}
                    y={nodeCY - 2}
                    style={{ fill: 'var(--text-secondary)' }}
                    fontSize={8}
                  >
                    {label.length > 14 ? label.slice(0, 13) + '.' : label}
                  </text>
                  {/* Raw value */}
                  <text
                    x={SIG_X + 6}
                    y={nodeCY + 9}
                    fill={valueColor}
                    fontSize={8}
                    fontFamily="monospace"
                  >
                    {rawFormatted}
                  </text>
                </g>
              </g>
            );
          })}

          {/* "+N more" / "Show less" indicator */}
          {truncatedCount > 0 && (() => {
            const lastSigY = signalPositions[visibleContribs[visibleContribs.length - 1]?.signal];
            return lastSigY !== undefined ? (
              <g cursor="pointer" onClick={() => setExpanded(true)}>
                <text
                  x={SIG_X + SIG_W / 2}
                  y={lastSigY + SIG_H / 2 + 16}
                  textAnchor="middle"
                  style={{ fill: 'var(--text-muted)', fontSize: '10px', fontStyle: 'italic' }}
                >
                  {`+${truncatedCount} more \u2014 click to expand`}
                </text>
              </g>
            ) : null;
          })()}
          {canCollapse && (() => {
            const lastSigY = signalPositions[visibleContribs[visibleContribs.length - 1]?.signal];
            return lastSigY !== undefined ? (
              <g cursor="pointer" onClick={() => setExpanded(false)}>
                <text
                  x={SIG_X + SIG_W / 2}
                  y={lastSigY + SIG_H / 2 + 16}
                  textAnchor="middle"
                  style={{ fill: 'var(--text-muted)', fontSize: '10px' }}
                >
                  Show less
                </text>
              </g>
            ) : null;
          })()}

          {/* ----------------------------------------------------------------
              LAYER 3: Signal → Interaction edges + SHAP badges
          ----------------------------------------------------------------- */}
          {(() => {
            // Track which signals already have a SHAP badge to avoid duplicates
            const renderedShapBadges = new Set<string>();
            return <>{visibleInteractions.flatMap(node => {
            const intTopY = interactionPositions[node.id];
            const intCY = intTopY + INT_H / 2;

            return node.signals
              .filter(s => signalPositions[s] !== undefined)
              .map(signal => {
                const c = contribMap[signal];
                if (!c) return null;
                const meta = signalMetadata[c.signal];
                const sigY = signalPositions[signal];
                const color = edgeColor(c.risk_score, isDark);
                const thickness = edgeThickness(c.weighted_contribution);
                const opacity = edgeOpacity(c.risk_score, c.weighted_contribution);
                const glow = hasGlow(c.weighted_contribution);
                const edgeVisible = isHighlighted('signal', signal) && isHighlighted('interaction', node.id);

                // SHAP badge position: next to the signal node's right edge, at the signal's Y
                const badgeX = SIG_X + SIG_W + 4;
                const badgeY = sigY - 7;
                const shapVal_ = Math.round(c.weighted_contribution * 100);
                const showShap = shapVal_ > 0 && c.risk_score > 0.1 && !renderedShapBadges.has(signal);
                if (showShap) renderedShapBadges.add(signal);
                const shapVal = Math.round(c.weighted_contribution * 100);
                // Red/neutral = +X (contributing to risk), green = -X (protective)
                const shapLabel = c.risk_score < 0.15 ? `\u2212${shapVal}` : `+${shapVal}`;
                const shapColor = c.risk_score > 0.3 ? '#ef4444' : c.risk_score < 0.15 ? green : (isDark ? '#888' : '#666');

                return (
                  <g
                    key={`${node.id}-${signal}`}
                    opacity={edgeVisible ? 1 : 0.15}
                    style={{ transition: 'opacity 0.15s ease' }}
                  >
                    <g
                      onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                        `${meta?.label || signal} \u2192 ${node.label}`,
                        `Risk: ${(c.risk_score * 100).toFixed(0)}%`,
                      ])}
                      onMouseLeave={hideTooltip}
                      cursor="pointer"
                    >
                      <path
                        d={`M${SIG_X + SIG_W},${sigY} C${SIG_X + SIG_W + 60},${sigY} ${INT_X - 30},${intCY} ${INT_X},${intCY}`}
                        stroke={color}
                        strokeWidth={thickness}
                        strokeOpacity={opacity}
                        fill="none"
                        filter={glow ? (c.risk_score > 0.3 ? 'url(#glow-red)' : c.risk_score < 0.15 ? 'url(#glow-green)' : undefined) : undefined}
                      />
                    </g>
                    {showShap && (
                      <g
                        opacity={dimOpacity('signal', signal)}
                        style={{ transition: 'opacity 0.15s ease' }}
                      >
                        <g
                          onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                            `SHAP contribution: ${shapVal > 0 ? '+' : ''}${shapVal} points to risk score`,
                          ])}
                          onMouseLeave={hideTooltip}
                          cursor="pointer"
                        >
                          <rect
                            x={badgeX}
                            y={badgeY}
                            width={20}
                            height={13}
                            style={{ fill: 'var(--bg-card)' }}
                            stroke={shapColor}
                            strokeWidth={0.5}
                            rx={0}
                          />
                          <text
                            x={badgeX + 10}
                            y={badgeY + 10}
                            textAnchor="middle"
                            fill={shapColor}
                            fontSize={7}
                            fontFamily="monospace"
                          >
                            {shapLabel}
                          </text>
                        </g>
                      </g>
                    )}
                  </g>
                );
              });
          })}</>;
          })()}

          {/* ----------------------------------------------------------------
              LAYER 3: Interaction nodes + multiplier badges
          ----------------------------------------------------------------- */}
          {visibleInteractions.map(node => {
            const intTopY = interactionPositions[node.id];
            const intCY = intTopY + INT_H / 2;

            // Find matching penalty for multiplier badge.
            // Match by keywords from the node's constituent signals rather than label,
            // since penalty strings describe signal combinations not node names.
            const PENALTY_KEYWORDS: Record<string, string[]> = {
              recovery_deficit: ['sleep', 'hrv', 'recovery'],
              overload_risk: ['load', 'spike', 'acwr', 'overtraining'],
              autonomic_stress: ['rhr', 'autonomic', 'hrv declining'],
              subjective_decline: ['subjective', 'soreness'],
              tissue_stress: ['regional', 'recurrence', 'injured area'],
            };
            const keywords = PENALTY_KEYWORDS[node.id] || [node.label.toLowerCase().split(' ')[0]];
            const matchedPenalty = interactionPenalties.find(p => {
              const lower = p.toLowerCase();
              return keywords.some(kw => lower.includes(kw));
            });
            const multiplier = matchedPenalty ? extractMultiplier(matchedPenalty) : null;

            return (
              <g
                key={node.id}
                opacity={dimOpacity('interaction', node.id)}
                style={{ transition: 'opacity 0.15s ease' }}
              >
                <g
                  onMouseEnter={(e: React.MouseEvent) => {
                    const signalLabels = node.signals
                      .map(s => signalMetadata[s]?.label || s.replace(/_/g, ' '))
                      .slice(0, 5); // cap at 5 to keep tooltip manageable
                    const moreCount = node.signals.length - signalLabels.length;
                    showTooltip(e, [
                      `${node.label}`,
                      `${node.description}`,
                      `Active signals:`,
                      ...signalLabels.map(l => `  - ${l}`),
                      ...(moreCount > 0 ? [`  + ${moreCount} more`] : []),
                    ]);
                    setHoveredElement({ type: 'interaction', id: node.id });
                  }}
                  onMouseLeave={() => {
                    hideTooltip();
                    setHoveredElement(null);
                  }}
                  cursor="pointer"
                >
                  <rect
                    x={INT_X}
                    y={intTopY}
                    width={INT_W}
                    height={INT_H}
                    style={{ fill: 'var(--accent-primary)' }}
                    stroke={isDark ? '#262626' : '#d4d4d4'}
                    strokeWidth={1}
                    rx={0}
                  />
                  <text
                    x={INT_X + 8}
                    y={intCY - 6}
                    style={{ fill: 'var(--text-secondary)' }}
                    fontSize={9}
                  >
                    {node.label.length > 18 ? node.label.slice(0, 17) + '.' : node.label}
                  </text>
                  <text
                    x={INT_X + 8}
                    y={intCY + 8}
                    style={{ fill: 'var(--text-muted)' }}
                    fontSize={8}
                  >
                    {node.description.length > 22 ? node.description.slice(0, 21) + '.' : node.description}
                  </text>

                  {/* Multiplier badge */}
                  {multiplier !== null && (
                    <g
                      onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                        `Cross-signal multiplier: ${multiplier}\u00d7`,
                        `${matchedPenalty || ''}`,
                      ])}
                      onMouseLeave={hideTooltip}
                      cursor="pointer"
                    >
                      <rect
                        x={INT_X + INT_W - 28}
                        y={intTopY - 8}
                        width={28}
                        height={14}
                        style={{ fill: 'var(--bg-card)' }}
                        stroke="rgba(239,68,68,0.5)"
                        strokeWidth={0.5}
                        rx={0}
                      />
                      <text
                        x={INT_X + INT_W - 14}
                        y={intTopY}
                        textAnchor="middle"
                        fill="#ef4444"
                        fontSize={7}
                        fontFamily="monospace"
                      >
                        {`x${multiplier.toFixed(1)}`}
                      </text>
                    </g>
                  )}
                </g>
              </g>
            );
          })}

          {/* ----------------------------------------------------------------
              LAYER 4: Interaction → Insight edges
          ----------------------------------------------------------------- */}
          {visibleInteractions.map(node => {
            const intTopY = interactionPositions[node.id];
            const intCY = intTopY + INT_H / 2;

            // Use average risk of member signals
            const memberRisks = node.signals
              .filter(s => contribMap[s])
              .map(s => contribMap[s].risk_score);
            const avgRisk = memberRisks.length
              ? memberRisks.reduce((a, b) => a + b, 0) / memberRisks.length
              : riskScore;
            const memberWeights = node.signals
              .filter(s => contribMap[s])
              .map(s => contribMap[s].weighted_contribution);
            const avgWeight = memberWeights.length > 0
              ? memberWeights.reduce((a, b) => a + b, 0) / memberWeights.length
              : 0;

            const color = edgeColor(avgRisk, isDark);
            const thickness = edgeThickness(avgWeight);
            const opacity = edgeOpacity(avgRisk, avgWeight);
            const edgeVisible = isHighlighted('interaction', node.id) && isHighlighted('insight', 'insight');

            return (
              <g
                key={`int-ins-${node.id}`}
                opacity={edgeVisible ? 1 : 0.15}
                style={{ transition: 'opacity 0.15s ease' }}
              >
                <g
                  onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                    `${node.label} \u2192 Risk Score`,
                    `Combined contribution: ${(avgWeight * 100).toFixed(0)}%`,
                  ])}
                  onMouseLeave={hideTooltip}
                  cursor="pointer"
                >
                  <path
                    d={`M${INT_X + INT_W},${intCY} C${INT_X + INT_W + 60},${intCY} ${INS_X - 30},${insightY + INS_H / 2} ${INS_X},${insightY + INS_H / 2}`}
                    stroke={color}
                    strokeWidth={thickness}
                    strokeOpacity={opacity}
                    fill="none"
                  />
                </g>
              </g>
            );
          })}

          {/* Draw direct signal→insight edges ONLY when no interaction nodes exist.
              When interactions are present, orphaned signals still show their node + SHAP badge
              but don't draw misleading direct edges to insight. */}
          {(() => {
            if (visibleInteractions.length > 0) return null;
            const orphanedSignals = visibleContribs.slice(0, 3);
            return orphanedSignals.map(c => {
            const meta = signalMetadata[c.signal];
            const sigY = signalPositions[c.signal];
            const color = edgeColor(c.risk_score, isDark);
            const thickness = edgeThickness(c.weighted_contribution);
            const opacity = edgeOpacity(c.risk_score, c.weighted_contribution);
            const edgeVisible = isHighlighted('signal', c.signal) && isHighlighted('insight', 'insight');

            const shapVal = Math.round(c.weighted_contribution * 100);
            const showShap = shapVal > 0 && c.risk_score > 0.1;
            const shapLabel = c.risk_score < 0.15 ? `\u2212${shapVal}` : `+${shapVal}`;
            const shapColor = c.risk_score > 0.3 ? '#ef4444' : c.risk_score < 0.15 ? green : (isDark ? '#888' : '#666');
            const badgeX = SIG_X + SIG_W + 4;
            const badgeY = sigY - 7;

            return (
              <g
                key={`direct-${c.signal}`}
                opacity={edgeVisible ? 1 : 0.15}
                style={{ transition: 'opacity 0.15s ease' }}
              >
                <g
                  onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                    `${meta?.label || c.signal} \u2192 Risk Score`,
                    `Contribution: ${(c.weighted_contribution * 100).toFixed(0)}% of score`,
                  ])}
                  onMouseLeave={hideTooltip}
                  cursor="pointer"
                >
                  <path
                    d={`M${SIG_X + SIG_W},${sigY} C${SIG_X + SIG_W + 80},${sigY} ${INS_X - 40},${insightY + INS_H / 2} ${INS_X},${insightY + INS_H / 2}`}
                    stroke={color}
                    strokeWidth={thickness}
                    strokeOpacity={opacity}
                    fill="none"
                  />
                </g>
                {showShap && (
                  <g
                    onMouseEnter={(e: React.MouseEvent) => showTooltip(e, [
                      `SHAP contribution: ${shapLabel} points to risk score`,
                    ])}
                    onMouseLeave={hideTooltip}
                    cursor="pointer"
                  >
                    <rect x={badgeX} y={badgeY} width={20} height={13}
                      style={{ fill: 'var(--bg-card)' }} stroke={shapColor} strokeWidth={0.5} />
                    <text x={badgeX + 10} y={badgeY + 10} textAnchor="middle"
                      fill={shapColor} fontSize={7} fontFamily="monospace">
                      {shapLabel}
                    </text>
                  </g>
                )}
              </g>
            );
          })})()}

          {/* ----------------------------------------------------------------
              LAYER 4: Insight node
          ----------------------------------------------------------------- */}
          <g
            opacity={dimOpacity('insight', 'insight')}
            style={{ transition: 'opacity 0.15s ease' }}
          >
            <g
              onMouseEnter={(e: React.MouseEvent) => {
                showTooltip(e, [
                  `Risk Score: ${riskPct}/100`,
                  `Tier: ${tier}`,
                  ...(deltaPct != null ? [`Change: ${deltaPct > 0 ? '+' : ''}${deltaPct} vs previous`] : []),
                ]);
                setHoveredElement({ type: 'insight', id: 'insight' });
              }}
              onMouseLeave={() => {
                hideTooltip();
                setHoveredElement(null);
              }}
              cursor="pointer"
            >
              <rect
                x={INS_X}
                y={insightY}
                width={INS_W}
                height={INS_H}
                style={{ fill: 'var(--accent-primary)' }}
                stroke={tierColor}
                strokeWidth={1}
                strokeOpacity={0.5}
                rx={0}
              />
              {/* Risk score large number */}
              <text
                x={INS_X + INS_W / 2}
                y={insightY + 30}
                textAnchor="middle"
                fill={tierColor}
                fontSize={28}
                fontWeight="bold"
                fontFamily="monospace"
              >
                {riskPct}
              </text>
              {/* Tier label */}
              <text
                x={INS_X + INS_W / 2}
                y={insightY + 48}
                textAnchor="middle"
                fill={tierColor}
                fontSize={9}
                fontFamily="monospace"
                style={{ opacity: 0.8 }}
              >
                {tier}
              </text>
              {/* Delta */}
              {deltaPct !== null && (
                <text
                  x={INS_X + INS_W / 2}
                  y={insightY + 64}
                  textAnchor="middle"
                  fill={deltaPct > 0 ? '#ef4444' : deltaPct < 0 ? green : (isDark ? '#555' : '#999')}
                  fontSize={8}
                  fontFamily="monospace"
                >
                  {deltaPct > 0 ? `+${deltaPct}` : `${deltaPct}`} vs prev
                </text>
              )}
            </g>
          </g>
        </svg>
      </div>
      </>
      )}
    </div>
  );
};

export default SignalFlowNetwork;
